<template>
  <LayoutColumns :loading="!categories">
    <template #leftSidebar>
      <SidebarMenu
        :top-list="sorts"
        :bottom-list="categories"
        :is-active-item-hidden="isActiveCategoryHidden"
      >
        <template v-if="isShowEventsFilter">
          <VideoEventsFilter
            :items="videoStore.eventsFilterItems"
            :is-active-item-hidden="isActiveEventsHidden"
            class="border-t-2 border-ocean pl-4 pt-6 mt-4"
          />
        </template>
      </SidebarMenu>
    </template>
    <LayoutWithRightSidebar>
      <template v-if="!isVideoItemPage" #rightSidebar>
        <NewsRightSidebar />
      </template>
      <div ref="topbar">
        <TopbarMenu :menu-list="topbarMenu">
          <template v-if="isShowEventsFilter">
            <TopbarDropdown
              :title="t('menu.events')"
              :hide-on-click="false"
              width="xl"
            >
              <VideoEventsFilter
                :items="videoStore.eventsFilterItems"
                is-scroll-view
                class="p-1"
              />
            </TopbarDropdown>
          </template>
        </TopbarMenu>
        <NuxtPage />
      </div>
    </LayoutWithRightSidebar>
  </LayoutColumns>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ComputedRef } from 'vue'
import { useNavigation } from '~/stores/navigation'
import { sidebarBottomListMaxLength, TopbarMenuInterface } from '~/models/menu'
import { useEvents } from '~/stores/events'
import { useAuth } from '~/stores/auth'
import { useVideo } from '~/stores/video'
import { NavigationSortType } from '~/models/navigation'
import { EventsListTypeMap } from '~/models/events'

const router = useRouter()
const { t } = useI18n()
const authStore = useAuth()
const { getSortItems, getCategoryItems, isCategoryHidden, fetchCategories } =
  useNavigation()

const navStore = useNavigation()
const { getEventsByType } = useEvents()
const videoStore = useVideo()
const topbar = ref<HTMLElement | null>(null)

const fetchEventsFilterItems = async () => {
  if (!videoStore.eventsFilterItems.length) {
    const events = await getEventsByType(EventsListTypeMap.Me)
    const filterItems = events
      ? events.map(({ id, name }) => ({
          id,
          label: name,
          value: false,
        }))
      : []
    videoStore.setEventsFilterItems(filterItems)
  }
}

// we use process.server instead of onServerPrefetch to wait fetchCategories before further steps
if (process.server) {
  await fetchCategories()
  authStore.auth && (await fetchEventsFilterItems())
} else {
  await fetchCategories()
  authStore.auth && fetchEventsFilterItems()
}

const sorts = getSortItems('video', [
  NavigationSortType.Popular,
  NavigationSortType.New,
])

const categories = getCategoryItems('video')

const currentCategorySlug = computed(() => {
  const category = router.currentRoute.value.params.category as string

  if (
    category &&
    Object.keys(navStore.categoriesSlugMap).length &&
    !navStore.categoriesSlugMap[category]
  ) {
    throw useCreateScreenError({
      message: `category ${category} is not found on video`,
    })
  }

  return category
})

const isActiveCategoryHidden = computed(
  () => isCategoryHidden(currentCategorySlug.value).value
)

const isActiveEventsHidden = computed(() => {
  const bottomPartIds = videoStore.eventsFilterItems
    .slice(sidebarBottomListMaxLength)
    .map(({ id }) => id)

  return videoStore.eventsFilterActiveIds.some((id) =>
    bottomPartIds.includes(id)
  )
})

const topbarMenu: ComputedRef<TopbarMenuInterface[]> = computed(() => [
  {
    title: t('menu.all_videos'),
    items: sorts.value,
    style: 'col',
  },
  {
    title: t('common.categories'),
    items: categories.value,
    style: 'raw',
    width: 'xl',
  },
])

const isVideoItemPage = computed(() =>
  router.currentRoute.value.name?.toString().includes('item-id')
)

const isShowEventsFilter = computed(() => {
  return (
    authStore.auth &&
    !isVideoItemPage.value &&
    videoStore.eventsFilterItems.length
  )
})

watch(
  () => !!authStore.auth,
  () => {
    if (authStore.auth) {
      fetchEventsFilterItems()
    }
  }
)
</script>
